import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

import styles from "./SingleBrandPage.module.css";

import AgeSign from "../../asset/icons/18PlusSign.png";
import RupeeCycle from "../../asset/icons/RupyeeCycle.png";
import FleekTiltIcon from "../../asset/icons/fleekTiltIcon.png";
import { useParams } from "react-router-dom";
import { ReadMoreComp } from "../../components/ReadMoreComp/ReadMoreComp";
import { ErrorFallback } from "../../components/Error/Error";
import { Loading } from "../../components/Loading/Loading";
import {
  DigitalBrandPlan,
  DigitalBrandBenefit,
  BrandAgeConBottomSheet,
  DigitalBrandFaq,
  TermNConditions,
  D2CSubscriptionBoxCard,
  BrandUserDetailsBottomSheet,
} from "../../components";
import { BrandImgInfoBanner } from "../../commonComponents";
import Api from "../../utils/Api/Api";
import Helper from "../../utils/Helper";
import { facebookCAPI } from "../../utils/FacebookCAPI"; // Import Facebook CAPI
import ShareChatAnalytics from "../../utils/ShareChatAnalytics";
import UTMTracker from "../../utils/UTMTracker";
import SnapchatPixel from '../../utils/SnapchatPixel';
import ConfettiHelper from '../../utils/ConfettiHelper';
import * as amplitude from '@amplitude/analytics-browser';

// Skeleton loader for SingleBrandPage
const BrandPageSkeleton = () => {
  return (
    <div className={styles.brandPage}>
      {/* Banner Skeleton */}
      <div className={styles.bannerSkeleton}>
        <div className={styles.bannerImageSkeleton}></div>
        <div className={styles.bannerContentSkeleton}>
          <div className={styles.logoSkeleton}></div>
          <div className={styles.brandNameSkeleton}></div>
          <div className={styles.brandDescSkeleton}></div>
        </div>
      </div>
      
      {/* Content Skeleton */}
      <div className={styles.content}>
        {/* Discount Banner Skeleton */}
        <div className={styles.discountSkeleton}></div>
        
        {/* Plans Section Skeleton */}
        <div className={styles.planSection}>
          <div className={styles.sectionTitleSkeleton}></div>
          <div className={styles.planList}>
            {[1, 2, 3].map((_, idx) => (
              <div key={idx} className={styles.planCardSkeleton}>
                <div className={styles.planHeaderSkeleton}></div>
                <div className={styles.planPriceSkeleton}></div>
                <div className={styles.planDescSkeleton}></div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Terms Skeleton */}
        <div className={styles.termsSkeleton}>
          <div className={styles.sectionTitleSkeleton}></div>
          <div className={styles.termsContentSkeleton}></div>
        </div>
        
        {/* Overview Skeleton */}
        <div className={styles.overViewSection}>
          <div className={styles.sectionTitleSkeleton}></div>
          <div className={styles.overViewTextSkeleton}></div>
        </div>
        
        {/* Benefits Skeleton */}
        <div className={styles.benefitSection}>
          <div className={styles.sectionTitleSkeleton}></div>
          <div className={styles.benefitsListSkeleton}>
            {[1, 2, 3].map((_, idx) => (
              <div key={idx} className={styles.benefitItemSkeleton}></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const SingleBrandPage = ({ isLoggedIn }) => {
  const { vendor_name } = useParams();
  const navigate = useNavigate();

  const [brandData, setBrandData] = useState({
    name: "",
    banner_img: "",
    logo: "",
    desc: "",
    plans: [],
    termNCond: [],
    faqList: [],
  });
  const [showModal, setShowModal] = useState(false);
  const [extraDetailsReq, setExtraDetailsReq] = useState(false);
  const [extraDetailsData, setExtraDetailsData] = useState({});
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const [modalData, setModalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasError, setError] = useState(false);
  const [selectedPlanIdx, setSelectedPlan] = useState(-1);
  const [isD2c, setIsD2c] = useState(false);

  const boxes = [1, 2, 3, 4, 5, 6];
  localStorage.removeItem("bundleId");

  const toggleModal = () => {
    setShowModal((pre_state) => !pre_state);
  };
  const AddToCart = async (details) => {
    try {
      console.log("SingleBrandPage AddToCart called");
      
      // Handle Chess.com affiliate redirect
      if (vendor_name === 'chess.com') {
        // Track Chess.com affiliate click from BUY NOW button
        amplitude.track('Chess.com Affiliate Click', {
          source: 'buy_now_button',
          component: 'SingleBrandPage',
          vendor_name: vendor_name,
          affiliate_url: 'https://chess.com/membership?ref_id=372424947',
          selected_plan_idx: selectedPlanIdx,
          page_url: window.location.href,
          timestamp: new Date().toISOString()
        });
        
        window.open('https://chess.com/membership?ref_id=372424947', '_blank');
        return;
      }
      
      // Prevent multiple clicks
      if (isAddingToCart) return;
      setIsAddingToCart(true);
      
      // Show confetti blast on click
      ConfettiHelper.smallBlast();
      
      if (!isLoggedIn) {
        // Track Login Required event (critical funnel drop-off point)
        amplitude.track('Login Required', {
          trigger_source: 'add_to_cart',
          plan_id: details?.subscription_plan || 'unknown',
          plan_name: selectedPlan?.plan_name || 'unknown',
          value: selectedPlan?.discounted_cost || 0,
          currency: selectedPlan?.currency_type || 'INR',
          page_url: window.location.href,
          timestamp: new Date().toISOString(),
          source: 'web'
        });
        
        // Save more complete redirect information
        localStorage.setItem("brandId", vendor_name);
        localStorage.setItem("returnToPage", `/brand/${vendor_name}`);
        localStorage.setItem("pendingAction", "addToCart");
        localStorage.setItem("pendingActionDetails", JSON.stringify({
          subscription_plan: details.subscription_plan,
          quantity: details.quantity,
          selectedPlanIdx: selectedPlanIdx
        }));
        
        console.log("User not logged in, redirecting to login with return path set");
        setIsAddingToCart(false);
        return navigate("/login");
      }

      if (activeSubscription) {
        setShowModal(true);
        setIsAddingToCart(false);
        return;
      }
      
      if (
        extra_details_required &&
        extra_details_required.length &&
        !(
          extra_details_required.length + 1 ===
          Object.keys(extraDetailsData).length
        )
      ) {
        setExtraDetailsReq(true);
        setExtraDetailsData((pre_state) => {
          return { ...pre_state, ...details };
        });
        setIsAddingToCart(false);
        return;
      }
      
      // Get selected plan data for tracking
      const selectedPlan = plans[selectedPlanIdx];
      
      //remove all items from cart
      await Api.EmptyCart();
      
      // add plan to cart
      await Api.AddToCart(details);

      // Track InitiateCheckout event with Facebook CAPI
      if (selectedPlan) {
        try {
          // Track with Facebook Pixel
          window.fbq('track', 'InitiateCheckout', {
            content_type: 'product',
            content_ids: [selectedPlan.subscription_plan_id],
            content_name: selectedPlan.plan_name,
            currency: selectedPlan.currency_type || 'INR',
            value: selectedPlan.discounted_cost || 0
          });
          
          // Track with Google Tag - Add to Cart
          if (window.gtag) {
            window.gtag('event', 'add_to_cart', {
              currency: selectedPlan.currency_type || 'INR',
              value: selectedPlan.discounted_cost || 0,
              items: [{
                item_id: selectedPlan.subscription_plan_id,
                item_name: selectedPlan.plan_name,
                price: selectedPlan.discounted_cost || 0,
                quantity: 1
              }]
            });
            
            // Track Google Ads conversion for Add to Cart (if you have a conversion label)
            window.gtag('event', 'conversion', {
              'send_to': 'AW-10792886696/add-to-cart-conversion-label', // Replace with your actual conversion label
              'value': selectedPlan.discounted_cost || 0,
              'currency': selectedPlan.currency_type || 'INR'
            });
          }
          
          // Also track with CAPI
          const planData = {
            content_ids: [selectedPlan.subscription_plan_id],
            content_name: selectedPlan.plan_name,
            currency: selectedPlan.currency_type || 'INR',
            value: selectedPlan.discounted_cost || 0,
            items: [{
              id: selectedPlan.subscription_plan_id,
              name: selectedPlan.plan_name,
              price: selectedPlan.discounted_cost,
              quantity: 1
            }]
          };
          
          facebookCAPI.trackInitiateCheckout(planData, {}).catch(error => {
            console.error("Error tracking InitiateCheckout event:", error);
          });

          // Track with ShareChat Analytics
          ShareChatAnalytics.trackAddToCart2(planData);
          
          // Track with Snapchat Pixel
          await SnapchatPixel.trackAddToCart(selectedPlan);

        } catch (error) {
          console.error("Error tracking events:", error);
        }
      }
      
      // Track Checkout Initiated (actual checkout navigation)
      amplitude.track('Checkout Initiated', {
        item_type: selectedPlan?.plan_name?.toLowerCase().includes('bundle') ? 'bundle' : 'plan',
        plan_id: selectedPlan?.subscription_plan_id,
        plan_name: selectedPlan?.plan_name,
        value: selectedPlan?.discounted_cost || 0,
        currency: selectedPlan?.currency_type || 'INR',
        mrp_price: selectedPlan?.mrp_price || 0,
        discount_amount: selectedPlan?.mrp_price && selectedPlan?.discounted_cost ? 
          (selectedPlan.mrp_price - selectedPlan.discounted_cost) : 0,
        service_name: selectedPlan?.service_name || 'unknown',
        page_url: window.location.href,
        timestamp: new Date().toISOString(),
        source: 'web',
      });

      // Show purchase celebration confetti
      ConfettiHelper.purchaseCelebration();
      
      navigate("/checkout");
    } catch (err) {
      console.log("Error in AddToCart:", err);
      setIsAddingToCart(false);
    }
  };

  useEffect(() => {
    // Capture UTM parameters on direct landing
    UTMTracker.captureUTMParams();
    
    // Check if we're returning from login with a pending action
    const pendingAction = localStorage.getItem("pendingAction");
    const pendingDetails = localStorage.getItem("pendingActionDetails");
    
    if (isLoggedIn && pendingAction === "addToCart" && pendingDetails) {
      try {
        console.log("Continuing pending addToCart action after login");
        
        // AMPLITUDE USER IDENTIFICATION FOR RETURNING USERS
        const phoneNo = localStorage.getItem('phoneNo');
        if (phoneNo) {
          amplitude.setUserId(phoneNo);
          
          // Track Post-Login Action Completed
          amplitude.track('Post-Login Action Completed', {
            action: 'add_to_cart',
            return_source: 'login_flow',
            brand_name: vendor_name,
            page_url: window.location.href,
            timestamp: new Date().toISOString(),
            source: 'web'
          });
        }
        
        const details = JSON.parse(pendingDetails);
        
        // Clean up localStorage
        localStorage.removeItem("pendingAction");
        localStorage.removeItem("pendingActionDetails");
        
        // Set the selected plan index first (important for UI state)
        if (details.selectedPlanIdx !== undefined) {
          setSelectedPlan(details.selectedPlanIdx);
        }
        
        // Add a small delay to ensure component is fully mounted
        setTimeout(() => {
          // Add to cart with the saved details
          AddToCart({
            subscription_plan: details.subscription_plan,
            quantity: details.quantity
          });
        }, 500);
      } catch (err) {
        console.error("Error processing pending action:", err);
      }
    }
    
    const fetchBrandData = async () => {
      try {
        // Handle Chess.com redirect before making API call
        if (vendor_name === 'chess.com') {
          // Track Chess.com affiliate click from direct page access
          amplitude.track('Chess.com Affiliate Click', {
            source: 'direct_page_access',
            component: 'SingleBrandPage',
            vendor_name: vendor_name,
            affiliate_url: 'https://chess.com/membership?ref_id=372424947',
            page_url: window.location.href,
            timestamp: new Date().toISOString()
          });
          
          window.location.href = 'https://chess.com/membership?ref_id=372424947';
          return;
        }
        
        let {
          data: { vendor_data, plans_data },
        } = await Api.BrandDetails({
          params: {
            vendor_name,
            show_out_of_stock: true,
            extra_details_for_web: true,
          },
        });

        // Track ViewContent event when plans are loaded
        if (plans_data?.length) {
          // Track with Facebook Pixel
          window.fbq('track', 'ViewContent', {
            content_type: 'product',
            content_ids: plans_data.map(plan => plan.subscription_plan_id),
            content_name: vendor_data.service.service_name,
            content_category: 'subscription_plan',
            currency: plans_data[0]?.currency_type || 'INR',
            value: plans_data[0]?.discounted_cost || 0
          });
          
          // Track with Snapchat Pixel
          await SnapchatPixel.trackViewContent({
            price: plans_data[0]?.discounted_cost || 0,
            currency: plans_data[0]?.currency_type || 'INR',
            item_ids: plans_data.map(plan => plan.subscription_plan_id),
            item_category: 'subscription_plan'
          });
        }

        // Check if any plan has USD currency and handle auto-login immediately
        const hasUSDPlan = plans_data.some(plan => plan.currency_type === 'usd');
        if (hasUSDPlan) {
          await Helper.checkAndHandleUSDCurrency('usd');
          // If we're not logged in and this is a USD plan, don't proceed with setting state
          // The page will reload after auto-login
          if (!localStorage.getItem('accesstoken')) {
            return;
          }
        }

        setBrandData({
          name: vendor_data.service.service_name,
          banner_img: vendor_data.detail_page_banner_url,
          logo: vendor_data.service.logo_url,
          desc: vendor_data.description,
          plans: plans_data,
          termNCond: vendor_data.terms_and_conditions,
          activeSubscription: vendor_data.is_already_availed_in_subscribe_flow,
          extra_details_required: vendor_data.extra_details_required,
          metaDetails: {
            title: vendor_data?.seo_title || "",
            content: vendor_data?.seo_description || "",
          },
          discount_percentage: vendor_data.discount_percentage,
          faqList: vendor_data.faq,
          overView: vendor_data.overview,
          benefits: vendor_data.benefits,
        });
        if (vendor_data.show_age_restrictions_popup) {
          setShowModal(true);
          setModalData({
            title: "You need to be above 18 years of age",
            text: "Disclaimer: This subscription may contain adult language and content. It is not suitable for anyone under the age of 18 and may not be suitable for all adult viewers, view at your discretion.",
            button2: {
              text: "NO I'M NOT",
              action: "/marketplace",
            },
            button1: {
              text: "YES, I AM ABOVE 18",
              action: vendor_data.vendor_id,
            },
            type: 1,
            image: AgeSign,
          });
        } else if (vendor_data.is_already_availed_in_subscribe_flow) {
          // setShowModal(true);
          setModalData({
            title: `You already have a recurring subscription for ${
              vendor_data.service.service_name
            }. ${
              vendor_data.due_date
                ? `Your next purchase will be done on ${Helper.dateFormat(
                    vendor_data.due_date
                  )}`
                : ""
            }`,
            text: "",
            button2: {
              text: "OKAY, GOT IT",
              action: "",
              cta: true,
            },
            type: 2,
            image: RupeeCycle,
          });
        }
      } catch (err) {
        setError(true);
      }
      setLoading(false);
    };
    fetchBrandData();
  }, [isLoggedIn]);

  if (hasError) {
    return <ErrorFallback />;
  }
  if (loading) {
    return <BrandPageSkeleton />;
  }
  const {
    benefits,
    plans,
    termNCond,
    overView,
    faqList,
    activeSubscription,
    discount_percentage,
    extra_details_required,
    metaDetails = {
      title: "",
      content: "",
    },
  } = brandData;
  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{metaDetails.title}</title>
        <meta content={metaDetails.content} name="description" />
        <meta content={metaDetails.title} property="twitter:title"></meta>
        <meta
          content={metaDetails.content}
          property="twitter:description"
        ></meta>
        <meta content="summary_large_image" name="twitter:card"></meta>
        <meta content={metaDetails.title} property="og:title"></meta>
        <meta content={metaDetails.content} property="og:description"></meta>
        <link rel="canonical" href={"/brand/" + vendor_name} />
        <meta property="og:type" content="website"></meta>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": brandData.name,
            "description": brandData.desc,
            "image": brandData.logo,
            "brand": {
              "@type": "Brand",
              "name": brandData.name,
              "logo": brandData.logo
            },
            "offers": plans.map(plan => ({
              "@type": "Offer",
              "price": plan.discounted_cost,
              "priceCurrency": plan.currency_type || "INR",
              "name": plan.plan_name,
              "availability": "https://schema.org/InStock"
            }))
          })}
        </script>
      </Helmet>
      {extraDetailsReq && (
        <BrandUserDetailsBottomSheet
          setExtraDetailsReq={setExtraDetailsReq}
          extra_details_required={extra_details_required}
          AddToCart={AddToCart}
          extraDetailsData={extraDetailsData}
          setExtraDetailsData={setExtraDetailsData}
        />
      )}
      {showModal && (
        <BrandAgeConBottomSheet
          toggleModal={toggleModal}
          modalData={modalData}
        />
      )}

      <div className={styles.displayDesktop}>{/* <NavBar/> */}</div>
      <div className={styles.brandPage}>
        <BrandImgInfoBanner isLoggedIn={isLoggedIn} brandData={brandData} />
        {!isD2c && (
          <div className={styles.content}>
            {discount_percentage ? (
              <div className={styles.fleekCoinContainer}>
                <div className={styles.fleekTextContainer}>
                  <div className={styles.fleekText}>
                    Get{" "}
                    <p className={styles.fleekBoldText}>
                      {discount_percentage}%
                    </p>{" "}
                    OFF
                  </div>
                  {/* <p className={styles.fleekOtherText}>using FleekCoins</p> */}
                </div>
                <img
                  className={styles.fleekTiltIcon}
                  src={FleekTiltIcon}
                  alt="FleekTiltIcon"
                />
              </div>
            ) : (
              ""
            )}
            <div className={styles.planSection}>
              <p className={styles.sectionTitle}>Plan</p>
              <div className={styles.planList}>
                {plans.map((ele, idx) => (
                  <DigitalBrandPlan
                    AddToCart={AddToCart}
                    plan={ele}
                    index={idx}
                    selectedPlanIdx={selectedPlanIdx}
                    setSelectedPlan={setSelectedPlan}
                    key={idx}
                  />
                ))}
              </div>
            </div>
            {termNCond.length > 0 && <TermNConditions terms={termNCond} />}
            {overView && overView.length && (
              <div className={styles.overViewSection}>
                <p className={styles.sectionTitle}>Overview</p>
                <div className={styles.overViewText}>
                  <ReadMoreComp cut={120} text={overView} />
                </div>
              </div>
            )}
            {selectedPlanIdx !== -1 && (
              <div className={styles.mobileButtonContainer}>
                <button
                  onClick={() =>
                    AddToCart({
                      subscription_plan: Number(
                        plans[selectedPlanIdx].subscription_plan_id
                      ),
                      quantity: plans[selectedPlanIdx].quantity,
                    })
                  }
                  className={`${styles.payButton} ${isAddingToCart ? styles.loadingButton : ''}`}
                  disabled={isAddingToCart}
                >
                  {isAddingToCart ? (
                    <span className={styles.loadingSpinner}></span>
                  ) : (
                    "BUY NOW"
                  )}
                </button>
              </div>
            )}
            {benefits && (
              <div className={styles.benefitSection}>
                <DigitalBrandBenefit benefits={benefits} />
              </div>
            )}
            {faqList && faqList.length ? (
              <div className={styles.faqsSection}>
                <DigitalBrandFaq faqList={faqList} />
              </div>
            ) : (
              ""
            )}
            {selectedPlanIdx !== -1 && <div className={styles.mobSpacer}></div>}
            <div className={styles.spacer}></div>
          </div>
        )}
        {isD2c && (
          <div className={styles.content}>
            <div className={styles.boxContainer}>
              <p className={styles.d2cTitle}>Select a subscription box</p>
              <div className={styles.boxes}>
                {boxes.map((ele, idx) => (
                  <D2CSubscriptionBoxCard key={idx} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
